import { Box } from "@mui/material";
import { Ruler } from "@procore/core-icons";
import { useEffect, useMemo } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars, useToolbarButtons } from "contexts/toolbars";
import SectionIcon from "media/icons/section.svg?react";

import { PROCORE_BOTTOM_ENTRY, PROCORE_TOOL_IDS } from "../constants";
import { BottomToolbarButton } from "./BottomToolbarButton";
import { bottomToolbarActions } from "./slice";

interface ToolsButtonRendererProps {
    toolbarId: string;
}

/**
 * Custom button renderer for tools content buttons
 */
function ToolsButtonRenderer({ toolbarId }: ToolsButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <Box display="flex" gap={`${PROCORE_BOTTOM_ENTRY.GAP}px`}>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (!state.visible) {
                    return null;
                }

                return (
                    <BottomToolbarButton
                        key={config.id}
                        icon={config.icon}
                        label={config.title || ""}
                        tooltip={config.tooltip?.toolName || config.title || ""}
                        active={state.active}
                        onClick={config.onClick}
                    />
                );
            })}
        </Box>
    );
}

/**
 * Content for the Tools tab - activates tools
 */
export function BottomToolbarToolsContent() {
    const dispatch = useAppDispatch();
    const toolbarDispatch = useDispatchToolbars();

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            // {
            //     config: {
            //         id: PROCORE_TOOL_IDS.MARKUP,
            //         icon: <Brush />,
            //         onClick: () => dispatch(bottomToolbarActions.setActiveTool(PROCORE_TOOL_IDS.MARKUP)),
            //         title: "Markup",
            //         tooltip: {
            //             toolName: "Markup tools",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            // {
            //     config: {
            //         id: PROCORE_TOOL_IDS.CREATE,
            //         icon: <Add />,
            //         onClick: () => dispatch(bottomToolbarActions.setActiveTool(PROCORE_TOOL_IDS.CREATE)),
            //         title: "Create",
            //         tooltip: {
            //             toolName: "Create objects",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            {
                config: {
                    id: PROCORE_TOOL_IDS.MEASURE,
                    icon: <Ruler />,
                    onClick: () => dispatch(bottomToolbarActions.setActiveTool(PROCORE_TOOL_IDS.MEASURE)),
                    title: "Measure",
                    tooltip: {
                        toolName: "Measurement tools",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: PROCORE_TOOL_IDS.SECTION,
                    icon: <SectionIcon />,
                    onClick: () => {
                        dispatch(bottomToolbarActions.setActiveTool(PROCORE_TOOL_IDS.SECTION));
                        void track(Events.toolbar.sectionTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Section",
                    tooltip: {
                        toolName: "Section tools",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
        ],
        [dispatch],
    );

    const toolbarId = "bottom-toolbar-tools";

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(toolbarId));
        };
    }, [toolbarDispatch, buttons]);

    return <ToolsButtonRenderer toolbarId={toolbarId} />;
}
